import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { auth } from '../api/client';
import { Button, Field } from '../components/ui';

/**
 * E-mail + code login (the notifications-service pattern). Two steps in one
 * screen: the code input appears after the letter is sent. No membership
 * oracle: the API answers {ok:true, sent:false} for unknown e-mails too.
 */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const requestCode = useMutation({
    mutationFn: () => auth.requestCode(email.trim().toLowerCase()),
    onSuccess: (result) => {
      if (!result.ok) {
        setError(result.error ?? 'Не удалось отправить код');
        return;
      }
      if (result.sent) {
        setStep('code');
        setNotice(`Код отправлен на ${email}. Он действует 10 минут.`);
      } else {
        setNotice('Если этот адрес в списке администраторов — письмо с кодом отправлено.');
      }
      setError(null);
    },
    onError: () => setError('Сервис отправки недоступен, попробуйте ещё раз'),
  });

  const verify = useMutation({
    mutationFn: () => auth.verify(email.trim().toLowerCase(), code.trim()),
    onSuccess: () => {
      window.location.assign('/');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Ошибка';
      setError(/401/.test(message) ? 'Неверный код или срок его действия истёк' : 'Не удалось проверить код');
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    if (step === 'email') requestCode.mutate();
    else verify.mutate();
  };

  return (
    <main className="login-wrap">
      <h1>Ops Monitor</h1>
      <p>Внутренний дашборд состояния платформ Айсмарт и КБ. Вход — по служебному e-mail и коду из письма.</p>
      <form onSubmit={submit} className="form-col" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Служебный e-mail" htmlFor="login-email">
          <input
            id="login-email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@ismart.org"
            disabled={step === 'code'}
          />
        </Field>
        {step === 'code' ? (
          <Field label="Код из письма" htmlFor="login-code">
            <input
              id="login-code"
              ref={codeRef}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              placeholder="000000"
            />
          </Field>
        ) : null}
        {notice ? <p>{notice}</p> : null}
        {error ? <p className="form-error">{error}</p> : null}
        <Button variant="primary" type="submit" loading={requestCode.isPending || verify.isPending}>
          {step === 'email' ? 'Получить код' : 'Войти'}
        </Button>
        {step === 'code' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStep('email');
              setCode('');
              setNotice(null);
            }}
          >
            Изменить адрес
          </Button>
        ) : null}
      </form>
    </main>
  );
}
